import { useEffect, useRef, useState } from "react";

/**
 * Custom hook for typing animation
 *
 * @param text - The text to animate
 * @param speed - The speed of typing in milliseconds per character (default: 50ms)
 */
export function useTypingAnimation(text: string, speed: number = 50) {
  const [displayText, setDisplayText] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Reset state if no text
    if (!text) {
      setIsTyping(false);
      setDisplayText("");
      return;
    }

    // Initialize typing
    setDisplayText("");
    setIsTyping(true);
    let currentIndex = 0;

    // Type first character immediately
    setDisplayText(text[0]);

    // Set up interval for remaining characters if any
    if (text.length > 1) {
      intervalRef.current = setInterval(() => {
        currentIndex++;
        setDisplayText(text.slice(0, currentIndex + 1));

        if (currentIndex >= text.length - 1) {
          setIsTyping(false);
          clearInterval(intervalRef.current!);
          intervalRef.current = null;
        }
      }, speed);
    } else {
      setIsTyping(false);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [text, speed]);

  return { displayText, isTyping };
}
